import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { FaTelegram, FaFacebookMessenger, FaInstagram } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiMessageSquare, FiMail, FiSmartphone, FiBell, FiZap, FiCheck, FiSend } = FiIcons;

const CommunicationHub = () => {
  const { t } = useLanguage();

  const CHANNELS = [
    {
      id: 'instagram',
      name: t('communicationSection.channels.instagram.name'),
      icon: FaInstagram,
      color: 'text-pink-600',
      bg: 'bg-pink-50',
      desc: t('communicationSection.channels.instagram.desc')
    },
    {
      id: 'whatsapp',
      name: t('communicationSection.channels.whatsapp.name'),
      icon: FiMessageSquare,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
      desc: t('communicationSection.channels.whatsapp.desc')
    },
    /* {
      id: 'email',
      name: t('communicationSection.channels.email.name'),
      icon: FiMail,
      color: 'text-indigo-500',
      bg: 'bg-indigo-50',
      desc: t('communicationSection.channels.email.desc')
    }, */
    /* {
      id: 'sms',
      name: t('communicationSection.channels.sms.name'),
      icon: FiSmartphone,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      desc: t('communicationSection.channels.sms.desc')
    }, */
    {
      id: 'telegram',
      name: t('communicationSection.channels.telegram.name'),
      icon: FaTelegram,
      color: 'text-sky-500',
      bg: 'bg-sky-50',
      desc: t('communicationSection.channels.telegram.desc')
    },
    {
      id: 'messenger',
      name: t('communicationSection.channels.messenger.name'),
      icon: FaFacebookMessenger,
      color: 'text-blue-500',
      bg: 'bg-blue-50',
      desc: t('communicationSection.channels.messenger.desc')
    }
  ];

  const MOCK_MESSAGES = [
    { sender: 'system', text: t('communicationSection.messages.m1'), time: '10:00 AM' },
    { sender: 'user', text: t('communicationSection.messages.m2'), time: '10:02 AM' },
    { sender: 'ai', text: t('communicationSection.messages.m3'), time: '10:02 AM' },
    { sender: 'system', text: t('communicationSection.messages.m4'), time: '6:00 PM' },
  ];

  const [activeChannelId, setActiveChannelId] = useState(CHANNELS[0].id);
  const activeChannel = CHANNELS.find(c => c.id === activeChannelId) || CHANNELS[0];
  const [visibleMessages, setVisibleMessages] = useState([]);

  useEffect(() => {
    setVisibleMessages([]);
    let timeout;
    MOCK_MESSAGES.forEach((msg, i) => {
      timeout = setTimeout(() => {
        setVisibleMessages(prev => [...prev, msg]);
      }, (i + 1) * 1500);
    });
    return () => clearTimeout(timeout);
  }, [activeChannelId]);

  return (
    <section id="communication" className="py-24 bg-slate-50 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          
          {/* Left Side: Content */}
          <div className="lg:w-1/2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold uppercase tracking-wider mb-6">
              <SafeIcon icon={FiZap} className="w-4 h-4" /> {t('communicationSection.badge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
              {t('communicationSection.title')} <br />
              <span className="text-gradient">{t('communicationSection.titleSpan')}</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
              {t('communicationSection.desc')}
            </p>

            <div className="space-y-4">
              {CHANNELS.map((channel) => (
                <button
                  key={channel.id}
                  onClick={() => setActiveChannelId(channel.id)}
                  className={`w-full flex items-start gap-4 p-5 rounded-2xl border transition-all duration-300 text-left ${
                    activeChannel.id === channel.id
                      ? 'bg-white border-indigo-200 shadow-xl shadow-indigo-500/5'
                      : 'border-transparent hover:bg-white/50'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${channel.bg} ${channel.color}`}>
                    <SafeIcon icon={channel.icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">{channel.name}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{channel.desc}</p>
                  </div>
                  {activeChannel.id === channel.id && (
                    <div className="ml-auto">
                      <SafeIcon icon={FiCheck} className="text-indigo-600 w-5 h-5" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Right Side: Phone Mockup */}
          <div className="lg:w-1/2 relative">
            <div className="absolute -inset-10 bg-indigo-500/10 rounded-full blur-3xl -z-10 animate-pulse"></div>
            
            <div className="relative mx-auto w-[320px] h-[640px] bg-slate-900 rounded-[3rem] border-[8px] border-slate-800 shadow-2xl overflow-hidden">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 rounded-b-2xl z-20"></div>
              
              {/* Phone Content */}
              <div className="h-full bg-slate-50 flex flex-col">
                <div className="bg-white px-6 pt-10 pb-4 border-b border-slate-100 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    activeChannel.id === 'whatsapp' ? 'bg-emerald-500' : 
                    activeChannel.id === 'email' ? 'bg-indigo-500' : 
                    activeChannel.id === 'sms' ? 'bg-amber-500' : 
                    activeChannel.id === 'telegram' ? 'bg-sky-500' : 
                    activeChannel.id === 'instagram' ? 'bg-pink-500' : 'bg-blue-500'
                  }`}>
                    {activeChannel.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{activeChannel.name}</div>
                    <div className="text-[10px] text-emerald-500 font-medium">{t('communicationSection.status')}</div>
                  </div>
                </div>

                <div className="flex-1 p-4 space-y-4 overflow-y-auto bg-[#e5ddd5] pattern-bg shadow-inner">
                  <AnimatePresence initial={false}>
                    {visibleMessages.map((msg, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        className={`flex flex-col ${
                          msg.sender === 'user' ? 'items-end' : 'items-start'
                        }`}
                      >
                        <div className={`max-w-[80%] p-3 rounded-2xl text-xs shadow-sm ${
                          msg.sender === 'user' 
                            ? 'bg-indigo-600 text-white rounded-tr-none' 
                            : msg.sender === 'ai'
                            ? 'bg-violet-100 text-violet-900 rounded-tl-none border border-violet-200'
                            : 'bg-white text-slate-800 rounded-tl-none'
                        }`}>
                          {msg.text}
                          <div className={`text-[8px] mt-1 text-right ${
                            msg.sender === 'user' ? 'text-indigo-100' : 'text-slate-400'
                          }`}>
                            {msg.time}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* Input Area */}
                <div className="p-4 bg-white border-t border-slate-100 flex items-center gap-2">
                  <div className="flex-1 h-10 bg-slate-100 rounded-full px-4 flex items-center">
                    <span className="text-xs text-slate-400">{t('communicationSection.typePlaceholder')}</span>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white">
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badges */}
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="absolute -right-8 top-1/4 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <SafeIcon icon={FiBell} className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-900">{t('communicationSection.openRate')}</div>
                  <div className="text-[8px] text-slate-500">{t('communicationSection.openRateSub')}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .pattern-bg {
          background-image: radial-gradient(#d1d5db 0.5px, transparent 0.5px);
          background-size: 10px 10px;
        }
      `}} />
    </section>
  );
};

export default CommunicationHub;